import {reaction, observable, computed, action} from 'mobx';
import {nonenumerable} from 'core-decorators';
import objectAssignDeep from 'utils/object-assign-deep';

import APIRequestState from '../_util/api-request';

/**
 * {} => {cards: []}
 */
class LoadUserPaymentMethodsState extends APIRequestState {
  get method() { return 'GET'; }
  get url() { return '/me/cards'; }
}

/**
 * {paymentNonce, paymentFraudData} => {card}
 */
class AddUserPaymentMethodState extends APIRequestState {
  get method() { return 'POST'; }
  get url() { return '/me/cards'; }
}

class RemoveUserPaymentMethodState extends APIRequestState {
  get method() { return 'DELETE'; }
  get url() { return '/me/cards'; }
}

class UserPaymentMethodsState {
  @observable _load;
  @observable _add;
  @observable _delete;

  @computed get isLoading() { return this._load && this._load.isLoading || false; }
  @computed get isLoaded() { return this._load && !!this._load.response || false; }
  @computed get error() { return this._load && this._load.error || null; }

  load(...args) {
    if (!this._load) this._load = new LoadUserPaymentMethodsState(this.ctx);
    return this._load.load(...args);
  }
  maybeLoad(...args) {
    if (!this._load) this._load = new LoadUserPaymentMethodsState(this.ctx);
    return this._load.maybeLoad(...args);
  }

  add(...args) {
    if (!this._add) this._add = new AddUserPaymentMethodState(this.ctx);
    return this._add.load(...args);
  }

  delete(...args) {
    if (!this._delete) this._delete = new RemoveUserPaymentMethodState(this.ctx);
    return this._delete.delete(...args);
  }

  reset() {
    if (this._load) this._load.reset();
  }

  @observable paymentMethods = [];

  @computed get cards() {
    return this.paymentMethods
      .filter(card => card.type !== 'paypal');
  }
  @computed get paypalAccounts() {
    return this.paymentMethods
      .filter(card => card.type === 'paypal');
  }

  abort() {
    if (this._add) {
      this._add.abort();
    }
    if (this._delete) {
      this._delete.abort();
    }
    if (this._load) {
      this._load.abort();
    }
  }

  @nonenumerable
  listeners = [];

  constructor(ctx) {
    Object.defineProperty(this, 'ctx', {
      value: ctx,
      enumerable: false,
    });

    this.listeners.push(
      reaction(
        () => this._load && this._load.response && this._load.response.cards,
        (cards) => {
          this.paymentMethods = Array.from(cards || []);
        },
        {
          name: 'Add cards to list when they\'re loaded',
        },
      ),
      reaction(
        () => this._add && this._add.response && this._add.response.card,
        (card) => {
          if (card) {
            this.paymentMethods.unshift(card);
            this._add.reset();
          }
        },
        {
          name: 'Add new card to list of cards when it\'s added to the user\'s vault',
        },
      ),
    );
  }

  @action deserialize(value) {
    const {
      _load, _add, _delete, ...rest
    } = value;
    objectAssignDeep(this, rest);
    if (_load) {
      this._load = new LoadUserPaymentMethodsState(this.ctx);
      objectAssignDeep(this._load, _load);
    }
    if (_add) {
      this._add = new AddUserPaymentMethodState(this.ctx);
      objectAssignDeep(this._add, _add);
    }
    if (_delete) {
      this._delete = new RemoveUserPaymentMethodState(this.ctx);
      objectAssignDeep(this._delete, _delete);
    }
  }

  dispose() {
    while (this.listeners.length) {
      this.listeners.pop()();
    }
  }
}

export default UserPaymentMethodsState;



// WEBPACK FOOTER //
// ./spa/state/user/payment-methods.jsx